import React, { useState, useEffect } from "react";
import { getJoinInfo, getDelegates, getUploadUrl } from "../api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Skeleton } from "../components/ui/skeleton";
import {
  Download,
  Mail,
  CheckCircle,
  FileText,
  ArrowRight,
} from "lucide-react";

const benefits = [
  "Protection juridique en cas de litige",
  "Accompagnement personnalisé",
  "Informations régulières sur vos droits",
  "Une voix forte pour défendre vos intérêts",
  "Réseau de solidarité entre salariés",
  "Cotisation déductible à 66% des impôts",
];

export default function NousRejoindre() {
  const [joinInfo, setJoinInfo] = useState(null);
  const [delegates, setDelegates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [joinData, delegatesData] = await Promise.all([
          getJoinInfo(),
          getDelegates(),
        ]);
        setJoinInfo(joinData);
        setDelegates(delegatesData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const downloadUrl = joinInfo?.downloadUrl && joinInfo.downloadUrl !== "#"
    ? getUploadUrl(joinInfo.downloadUrl)
    : "#";

  return (
    <div>
      {/* Page Header */}
      <section className="bg-gradient-to-br from-slate-50 via-sky-50/40 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100 px-4 py-1.5 text-xs font-semibold mb-4">
            Adhésion
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
            Nous rejoindre
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl">
            Rejoignez l'UNSA AEGIDE DOMITYS et faites entendre votre voix pour
            de meilleures conditions de travail.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-3 space-y-8">
              {loading ? (
                <div className="space-y-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-4/5" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ) : joinInfo ? (
                <div className="prose prose-slate max-w-none">
                  {joinInfo.text.split("\n\n").map((paragraph, index) => {
                    if (paragraph.includes("•")) {
                      const lines = paragraph.split("\n");
                      const intro = lines[0];
                      const items = lines.filter((l) => l.startsWith("•"));
                      return (
                        <div key={index} className="mb-6">
                          <p className="text-slate-600 leading-relaxed mb-4">
                            {intro}
                          </p>
                          <ul className="space-y-3">
                            {items.map((item, i) => (
                              <li
                                key={i}
                                className="flex items-start gap-3 text-slate-600"
                              >
                                <CheckCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                                <span>{item.replace("• ", "")}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    }
                    return (
                      <p
                        key={index}
                        className="text-slate-600 leading-relaxed mb-6"
                      >
                        {paragraph}
                      </p>
                    );
                  })}
                </div>
              ) : null}

              {/* Download CTA */}
              <Card className="border-sky-200 bg-sky-50/50 shadow-none">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-sky-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800 mb-2">
                        Bulletin d'adhésion
                      </h3>
                      <p className="text-sm text-slate-500 mb-4">
                        Téléchargez le bulletin, remplissez-le et remettez-le à
                        l'un de nos délégués syndicaux.
                      </p>
                      <a
                        href={downloadUrl}
                        target={downloadUrl !== "#" ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-sky-500 text-white text-sm font-semibold rounded-lg hover:bg-sky-600 transition-colors duration-200 shadow-sm"
                      >
                        <Download className="w-4 h-4" />
                        {joinInfo?.downloadLabel || "Télécharger le bulletin d'adhésion"}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              {/* Benefits Card */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">
                    Pourquoi adhérer ?
                  </h3>
                  <ul className="space-y-3">
                    {benefits.map((benefit, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 text-sm text-slate-600"
                      >
                        <CheckCircle className="w-4 h-4 text-sky-500 mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Separator />

              {/* Contact Delegates */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">
                    Contactez-nous
                  </h3>
                  {loading ? (
                    <div className="space-y-4">
                      {[1, 2].map((i) => (
                        <div key={i} className="flex items-center gap-3">
                          <Skeleton className="w-10 h-10 rounded-full" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-48" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {delegates.map((delegate) => (
                        <div
                          key={delegate.id}
                          className="flex items-center gap-3"
                        >
                          <img
                            src={delegate.photo}
                            alt={delegate.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {delegate.name}
                            </p>
                            <a
                              href={`mailto:${delegate.email}`}
                              className="text-xs text-sky-500 hover:text-sky-600 truncate block transition-colors"
                            >
                              {delegate.email}
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <Separator className="my-4" />

                  <a
                    href={`mailto:${joinInfo?.contactEmail || "contact@unsa-aegide-domitys.fr"}`}
                    className="inline-flex items-center gap-2 text-sm text-sky-500 hover:text-sky-600 font-medium transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    {joinInfo?.contactEmail || "contact@unsa-aegide-domitys.fr"}
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-sky-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">
            Prêt à nous rejoindre ?
          </h2>
          <p className="text-slate-500 mb-8 max-w-2xl mx-auto">
            N'hésitez pas à contacter nos délégués syndicaux pour toute
            question sur l'adhésion.
          </p>
          <a
            href={`mailto:${joinInfo?.contactEmail || "contact@unsa-aegide-domitys.fr"}`}
            className="inline-flex items-center gap-2 px-8 py-4 bg-sky-500 text-white font-semibold rounded-xl hover:bg-sky-600 transition-colors duration-200 shadow-lg shadow-sky-200/50"
          >
            Contactez-nous
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
}
